<template #default>
  <div class="page-view">
    <a-card class="page-filter" :bordered="false">
      <a-form
        layout="horizontal"
        :label-col="labelCol"
        :wrapper-col="wrapperCol"
        @submit.prevent="onSearch"
      >
        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item label="Name">
              <a-input
                v-model:value="query.displayCode"
                @pressEnter="onSearch"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="InstClass">
              <a-select
                v-model:value="query.externalInstClass"
                style="width: 100%"
                allow-clear
                placeholder="All"
                :show-search="true"
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="item in instClassOptions"
                  :key="item"
                  :value="item"
                >
                  {{ item }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item label="Exchange">
              <a-select
                v-model:value="query.exchangeCode"
                style="width: 100%"
                allow-clear
                placeholder="All"
                :show-search="true"
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="item in exchangeList"
                  :key="item.exchName"
                  :value="item.exchName"
                >
                  {{ item.exchName }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Currency">
              <a-select
                v-model:value="query.pnlCurrency"
                allow-clear
                :show-search="true"
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="item in currencyList"
                  :key="item.currencyCode"
                  :value="item.currencyCode"
                >
                  {{ item.currencyCode }} - {{ item.currencyDesc }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item
              :colon="false"
              :labelCol="{ span: 6, style: { textAlign: 'left' } }"
              :wrapperCol="{ span: 18 }"
            >
              <div class="page-actions">
                <a-button type="primary" @click="onSearch" :loading="loading"
                  >Search</a-button
                >
                <a-button @click="showAddModal">
                  <PlusOutlined />
                  Add
                </a-button>
                <a-button @click="showCopyModal">Copy</a-button>
              </div>
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <div class="page-grid page-grid--h450">
      <AgGridVue
        style="width: 100%; height: 100%"
        :theme="themeBalham"
        :loading="loading"
        :gridOptions="gridOptions"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="rowData"
      />
    </div>
    <IdentifierView
      v-model:visible="identifierModalVisible"
      :rawData="identifierRawData"
      @update:identifiers="handleUpdateIdentifiers"
    />
    <AddEditBasketIndexView
      v-model:visible="addBasketIndexModalVisible"
      :isEdit="isEdit"
      :addForm="addForm"
      @update:security="handleUpdateSecurity"
    />
  </div>
</template>

<script setup>
import { ref, defineExpose, onMounted, provide, computed } from 'vue'
import { useStore } from 'vuex'
import { themeBalham } from 'ag-grid-community'

import { message } from 'ant-design-vue'
import { PlusOutlined } from '@ant-design/icons-vue'

import { buildBasketIndexColumnDefs } from './BasketIndexView_ColumnDefs'
import AgEditCellRenderer from '@/components/AgEditCellRenderer.vue'
import AddEditBasketIndexView from './AddEditBasketIndexView.vue'
import IdentifierView from '../IdentifierView.vue'

import reference from '@/api/reference'
import security from '@/api/security'
import dayjs from 'dayjs'

const store = useStore()
const gridApi = ref(null)
const gridOptions = {
  rowSelection: 'single',

  onGridReady: (params) => {
    gridApi.value = params.api
  },
}

const defaultColDef = {
  filter: true,
  sortable: true,
  resizable: true,
}

defineExpose({
  AgEditCellRenderer,
})

const currencyList = computed(() => {
  const currencies = store.getters['reference/getCurrencyList'] || []
  return [...currencies].sort((a, b) =>
    a.currencyCode.localeCompare(b.currencyCode)
  )
})

const labelCol = {
  span: 6,
  style: { textAlign: 'left' },
}

const wrapperCol = {
  span: 18,
}

const loading = ref(false)
const rowData = ref([])
const instClassOptions = ref([])
const exchangeList = ref([])
const identifierTypeList = ref([])
const identifierRawData = ref({
  securityId: null,
  identifiers: [],
})
const addBasketIndexModalVisible = ref(false)
const addForm = ref({})
const isEdit = ref(false)
const identifierModalVisible = ref(false)

const query = ref({
  assetClass: 'BASKINDX',
  externalInstClass: null,
  exchangeCode: null,
  displayCode: null,
  pnlCurrency: null,
})

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback

const onSearch = async () => {
  loading.value = true
  try {
    const params = { ...query.value }
    if (!params.externalInstClass) {
      params.assetClass = 'BASKINDX'
      params.externalInstClass = null
    }
    if (
      !params.assetClass &&
      !params.externalInstClass &&
      !params.displayCode &&
      !params.exchangeCode &&
      !params.pnlCurrency
    ) {
      return
    }
    params.tickerOperator = 'START_WITH'
    const resp = await security.getSimpleSecurities(params)
    const data = resp.data
    rowData.value = Array.isArray(data) ? data : []
    if (rowData.value.length === 0) {
      message.info('No data found.')
    }
  } catch (e) {
    message.error(getErrorMessage(e, 'Query failed'))
  } finally {
    loading.value = false
  }
}

const showAddModal = () => {
  isEdit.value = false
  addForm.value = {
    securityId: null,
    externalSecurityId: null,
    assetClass: 'BASKINDX',
    issueDate: null,
    settleDate: null,
    maturityDate: null,
    exchange: {
      exchangeId: null,
      exchName: null,
      micExchName: null,
    },
    issuer: {
      issuerId: null,
      shortName: null,
      name: null,
    },
    underlyingTicker: null,
    instrumentClass: null,
    isin: null,
    ticker: null,
    name: null,
    desc: null,
    priceMultiplier: 1,
    contractSize: 1,
    tickSize: 1,
    tickValue: 1,
    pnlCurrency: null,
    isActive: true,
  }
  addBasketIndexModalVisible.value = true
}
const formatDateTime = (security) => {
  const format = (val) => (val ? dayjs(dayjs(val).format('YYYY-MM-DD')) : null)

  security.maturityDate = format(security.maturityDate)

  return security
}

const showEditModal = async (event) => {
  const data = event?.data || event
  const params = { securityId: data.securityId }
  try {
    const resp = await security.getSecurity(params)
    if (resp.data) {
      addForm.value = formatDateTime(resp.data)
      isEdit.value = true
      addBasketIndexModalVisible.value = true
    }
  } catch (error) {
    message.error(getErrorMessage(error, 'Failed to fetch security details'))
  }
}

const handleUpdateSecurity = async (newSecurity, isEdit) => {
  loading.value = true
  try {
    if (isEdit) {
      await security.updateSec(newSecurity)
      message.success('Security updated successfully')
      return
    }
    await security.createSec(newSecurity)
    message.success('Security created successfully')
  } catch (error) {
    message.error(
      getErrorMessage(
        error,
        isEdit ? 'Failed to update security' : 'Failed to create security'
      )
    )
  } finally {
    loading.value = false
  }
}

const showCopyModal = async () => {
  const selectedRows = gridApi.value?.getSelectedRows?.() || []
  if (selectedRows.length !== 1) {
    message.warning('Please select one row to copy')
    return
  }

  const params = { securityId: selectedRows[0].securityId }
  try {
    const resp = await security.getSecurity(params)
    if (resp.data) {
      const data = resp.data
      addForm.value = formatDateTime({
        ...data,
        securityId: undefined,
        externalSecurityId: undefined,
        exchange: { ...data.exchange },
        issuer: { ...data.issuer },
        identifiers: data.identifiers ? [...data.identifiers] : [],
      })
      isEdit.value = false
      addBasketIndexModalVisible.value = true
    }
  } catch (error) {
    message.error(getErrorMessage(error, 'Failed to fetch security details'))
  }
}

const filterOption = (input, option) => {
  const key = option?.key ?? option?.value ?? ''
  return String(key).toLowerCase().includes(input.toLowerCase())
}

const showIdentifierModal = async (event) => {
  const data = event?.data || event
  const params = { securityId: data.securityId }
  try {
    const resp = await security.getSecurity(params)
    identifierRawData.value.securityId = data.securityId
    identifierRawData.value.identifiers = resp.data.identifiers || []
    identifierModalVisible.value = true
  } catch (error) {
    message.error(getErrorMessage(error, 'Failed to fetch security details'))
  }
}

const handleUpdateIdentifiers = () => {}

const columnDefs = computed(() =>
  buildBasketIndexColumnDefs({
    onIdentifiers: showIdentifierModal,
    onEdit: showEditModal,
  })
)

onMounted(async () => {
  try {
    const result = await reference.getReferenceDataV2()
    instClassOptions.value = result.data?.basketIndexTypes || []
    exchangeList.value = result.data?.exchanges || []
    identifierTypeList.value = result.data?.identifierTypes || []
  } catch (e) {
    message.error(getErrorMessage(e, 'Failed to fetch reference lists'))
  }
})

provide('exchangeList', exchangeList)
provide('instClassOptions', instClassOptions)
provide('identifierTypeList', identifierTypeList)
provide('security', security)
</script>
